import { createTheme } from '@mui/material';
import type { DesignTokens } from '@weave/design-tokens';

import { getAccWeaveTheme } from './accTheme';
import type { ThemeId } from './constants';
import { WEAVE_THEME_ID, ALLOY_WEAVE3_THEME_ID } from './constants';
import type { AlloyTheme } from './types';
import { type WeaveTheme } from './types/weaveTypes';

type AlloyTokens = NonNullable<AlloyTheme['tokens']>;
type AlloyComponentTokens = AlloyTokens['component'];

/**
 * Merged themes replace raw Weave component entries with Alloy factory shapes from
 * `getAccWeaveTheme`. Typing that as intersection would keep conflicting Weave
 * keys; omit Alloy-mapped keys from raw `DesignTokens.component` first.
 */
export type CombinedTheme = Omit<WeaveTheme & AlloyTheme, 'tokens'> & {
  id: ThemeId;
  tokens?: Omit<DesignTokens, 'component'> &
    Omit<AlloyTokens, 'component'> & {
      component: Omit<DesignTokens['component'], keyof AlloyComponentTokens> &
        AlloyComponentTokens;
    };
};

export type MaybeCombinedTheme = WeaveTheme | CombinedTheme;

/**
 * `component.a` exists in a combined Alloy theme, not the raw Weave theme, so
 * its presence marks a theme as already combined. Weave 3 themes carry it too
 * (via `getAccWeave3Theme`'s Alloy-component fallback), but are also matched by
 * `id` as an explicit belt-and-suspenders in case that fallback ever changes.
 */
export const isCombinedTheme = (
  theme: MaybeCombinedTheme,
): theme is CombinedTheme => {
  const id = (theme as { id?: ThemeId }).id;
  return (
    (theme as AlloyTheme).tokens?.component?.a !== undefined ||
    id === ALLOY_WEAVE3_THEME_ID
  );
};

/**
 * Merges the Alloy component tokens onto a raw Weave theme (via MUI
 * `createTheme`'s deep merge) and tags the result with a `ThemeId`. A theme with
 * tokens is tagged `WEAVE_THEME_ID`; without tokens, `getAccWeaveTheme` supplies
 * the `ALLOY_THEME_ID` fallback that `createTheme` merges in.
 *
 * Idempotent: an already-combined theme is returned untouched, so it is safe on
 * nested providers or a theme already pulled from context. This lets
 * `WeaveThemeGate` combine once while `useTheme` short-circuits per descendant.
 */
export const combineWeaveTheme = (theme: MaybeCombinedTheme): CombinedTheme => {
  if (isCombinedTheme(theme)) {
    return theme;
  }

  const accWeaveTheme = getAccWeaveTheme(theme);

  return (
    theme.tokens
      ? createTheme(theme, accWeaveTheme, { id: WEAVE_THEME_ID })
      : createTheme(theme, accWeaveTheme)
  ) as CombinedTheme;
};
